<template>
  <div class="social-share-actions -flex -start">
    <!-- scroll to comments -->
    <div v-if="showCommentButton" class="share-action view-comments">
      <icon-button
        type="button"
        class="-ico-txt"
        icon="comment"
        :reduce="false"
        :label="`${commentCount}`"
        aria-label="View Comments"
        @click-action="goToComments"
      />
    </div>

    <!-- copy -->
    <div class="share-action" :class="copied ? 'success' : 'copy'">
      <icon-button
        type="button"
        :class="copied ? '-ico-txt' : '-ico-only'"
        :icon="copied ? 'task' : 'file_copy'"
        :label="copied ? 'Copied' : 'Copy'"
        :aria-label="copied ? 'Copied' : 'Copy Link'"
        @click-action="copyUrl"
      />
    </div>

    <!-- native device share -->
    <div v-if="canDeviceShare" class="share-action device-share">
      <icon-button
        type="button"
        class="-ico-only"
        icon="ios_share"
        label="Device Share"
        aria-label="Device Share"
        @click-action="deviceShare"
      />
    </div>

    <!-- social networks -->
    <div class="share-action facebook">
      <button
        type="button"
        class="btn -ico-only"
        aria-label="Share on Facebook"
        @click="openUrl('facebook')"
      >
        <i class="btn-ico svg-ico-facebook"></i>
      </button>
    </div>

    <div class="share-action twitter">
      <button
        type="button"
        class="btn -ico-only"
        aria-label="Share on X (Twitter)"
        @click="openUrl('twitter')"
      >
        <i class="btn-ico svg-ico-twitter"></i>
      </button>
    </div>

    <div class="share-action pinterest">
      <button
        type="button"
        class="btn -ico-only"
        aria-label="Share on Pinterest"
        @click="openUrl('pinterest')"
      >
        <i class="btn-ico svg-ico-pinterest"></i>
      </button>
    </div>

    <div class="share-action linkedin">
      <button
        type="button"
        class="btn -ico-only"
        aria-label="Share on LinkedIn"
        @click="openUrl('linkedin')"
      >
        <i class="btn-ico svg-ico-linkedin"></i>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 *
 * Social sharing buttons based off of shareon. Reference shareon github for any
 * inline SVG images. Currently supported:
 *
 *  - scroll to comments
 *  - click to copy
 *  - native device share
 *  - facebook
 *  - twitter
 *  - pinterest
 *  - linkedin
 *
 * @see https://github.com/kytta/shareon
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Navigator/share
 */
import { ref, computed } from 'vue'
import { scrollToElemTop } from '@/utils/scroll-to'
import { trackEvent } from '@/utils/analytics'
import IconButton from '@/components/buttons/icon-button.vue'

/** INLINE TYPES */
type ShareParams = {
  url: string
  title: string
}

/** PROPS */
const props = withDefaults(
  defineProps<{
    showCommentButton?: boolean
    commentCount?: number
    canonicalSharePath?: string
  }>(),
  {
    showCommentButton: true,
    commentCount: 0,
    canonicalSharePath: '',
  },
)

/** STATE */
const copied = ref(false)

/** NON REACTIVE */
const socialNetworks: Record<string, (sp: ShareParams) => string> = {
  bluesky: (sp) => `https://bsky.app/intent/compose?text=${sp.title}%0A%0A${sp.url}`,
  email: (sp) => `mailto:?subject=${sp.title}&body=${sp.url}`,
  facebook: (sp) => `https://www.facebook.com/sharer/sharer.php?u=${sp.url}`,
  hackernews: (sp) => `https://news.ycombinator.com/submitlink?u=${sp.url}&t=${sp.title}`,
  linkedin: (sp) => `https://www.linkedin.com/sharing/share-offsite/?url=${sp.url}`,
  mastodon: (sp) => `https://toot.kytta.dev/?text=${sp.title}%0D%0A${sp.url}`,
  // requires facebook app id
  // fbmessenger: (sp) => `https://www.facebook.com/dialog/send?app_id=${d.fbAppId}&link=${sp.url}&redirect_uri=${sp.url}`,
  pinterest: (sp) =>
    `https://pinterest.com/pin/create/button/?url=${sp.url}&description=${sp.title}`,
  pocket: (sp) => `https://getpocket.com/edit.php?url=${sp.url}`,
  reddit: (sp) => `https://www.reddit.com/submit?title=${sp.title}&url=${sp.url}`,
  teams: (sp) => `https://teams.microsoft.com/share?href=${sp.url}`,
  telegram: (sp) => `https://telegram.me/share/url?url=${sp.url}`,
  tumblr: (sp) =>
    `https://www.tumblr.com/widgets/share/tool?posttype=link&title=${sp.title}&content=${sp.url}&canonicalUrl=${sp.url}`,
  twitter: (sp) => `https://x.com/intent/post?url=${sp.url}&text=${sp.title}`,
  whatsapp: (sp) => `https://wa.me/?text=${sp.title}%0D%0A${sp.url}`,
}

/** COMPUTED */
const shareUrl = computed(() => {
  if (!props.canonicalSharePath) {
    return window.location.href.split('#')[0]
  }

  const shareUrl = new URL(props.canonicalSharePath, window.location.origin)
  return shareUrl.href
})

const shareTitle = computed(() => {
  return document.title
})

const canDeviceShare = computed(() => {
  if (!('share' in navigator)) {
    return false
  }

  if (!('canShare' in navigator)) {
    return false
  }

  return navigator.canShare({
    url: shareUrl.value,
    title: shareTitle.value,
  })
})

/** METHODS */
function trackClickEvent(label: string) {
  trackEvent({
    category: 'Social Share',
    action: shareUrl.value,
    label,
  })
}

// no tracking on comment anchor scroll
function goToComments() {
  const commentContainer = document.querySelector('#comments')

  if (!(commentContainer instanceof HTMLElement)) {
    return
  }

  scrollToElemTop({
    elem: commentContainer,
    _smoothScrollCallback: () => {
      window.location.hash = 'comments'
    },
  })
}

async function copyUrl() {
  // animation, wait until complete
  if (copied.value) {
    return
  }

  // plausible tracking
  trackClickEvent('copy')

  try {
    // Clipboard API is only supported by more modern browsers
    // @see https://caniuse.com/async-clipboard
    await navigator.clipboard.writeText(shareUrl.value)
  } catch (e) {
    // copy failed... no action
    console.error(e)
  } finally {
    copied.value = true

    // reset button
    setTimeout(() => {
      copied.value = false
    }, 2000)
  }
}

async function deviceShare() {
  // plausible tracking
  trackClickEvent('native')

  try {
    await navigator.share({
      url: shareUrl.value,
      title: shareTitle.value,
    })
  } catch (e) {
    console.error(e)
  }
}

function openUrl(networkKey: keyof typeof socialNetworks) {
  // plausible tracking
  trackClickEvent(networkKey)

  // build share url
  const socialNetworkShareUrl = socialNetworks[networkKey]({
    url: encodeURIComponent(shareUrl.value),
    title: encodeURIComponent(shareTitle.value),
  })

  // open in new tab
  window.open(socialNetworkShareUrl, '_blank', 'noopener,noreferrer')
}
</script>
